import React, { createContext, useContext, useState, ReactNode } from 'react';

interface LoadingContextType {
  isLoading: boolean;
  loadingMessage: string;
  showLoading: (message?: string) => void;
  hideLoading: () => void;
  activeRequests: number;
  incrementRequests: () => void;
  decrementRequests: () => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

interface LoadingProviderProps {
  children: ReactNode;
}

/**
 * Global loading state provider
 * Manages loading state for all API calls automatically
 * Uses a counter to track multiple simultaneous requests
 */
export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [activeRequests, setActiveRequests] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Chargement...');

  const showLoading = (message: string = 'Chargement...') => {
    setLoadingMessage(message);
  };

  const hideLoading = () => {
    setLoadingMessage('Chargement...');
  };

  const incrementRequests = () => {
    setActiveRequests((prev) => prev + 1);
  };

  const decrementRequests = () => {
    setActiveRequests((prev) => Math.max(0, prev - 1));
  };

  const isLoading = activeRequests > 0;

  return (
    <LoadingContext.Provider
      value={{
        isLoading,
        loadingMessage,
        showLoading,
        hideLoading,
        activeRequests,
        incrementRequests,
        decrementRequests,
      }}
    >
      {children}
    </LoadingContext.Provider>
  );
};

/**
 * Hook to access loading context
 * Use this in components to manually control loading state if needed
 */
export const useLoading = (): LoadingContextType => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};
